import { TASK_PRIORITY_WEIGHT } from 'modules/shared/constants';
import { TASK_STATUS } from 'modules/work-implementation/constants';

/**
 * Sorts tasks by priority (highest first).
 *
 * @param {Array} tasks
 * @returns {Array}
 */
export function sortTasksByPriority(tasks) {
  return [...tasks].sort(
    (a, b) => TASK_PRIORITY_WEIGHT[b.priority] - TASK_PRIORITY_WEIGHT[a.priority],
  );
}

/**
 * Calculates a priority score for a project based on its tasks.
 *
 * @param {Array} tasks
 * @returns {number}
 */
export function getProjectPriorityScore(tasks) {
  return tasks.reduce((score, task) => score + (TASK_PRIORITY_WEIGHT[task.priority] || 0), 0);
}

/**
 * Returns true if the task is considered finished.
 *
 * @param {string} status
 * @returns {boolean}
 */
export function isTaskCompleted(status) {
  return (
    status === TASK_STATUS.COMPLETED ||
    status === TASK_STATUS.APPROVED ||
    status === TASK_STATUS.SUBMITTED
  );
}

/**
 * Returns true if the task is actively running.
 *
 * @param {string} status
 * @returns {boolean}
 */
export function isTaskInProgress(status) {
  return status === TASK_STATUS.IN_PROGRESS;
}

/**
 * Converts HH:MM:SS or HH:MM to minutes.
 *
 * @param {string | null} time
 * @returns {number}
 */
export function timeStringToMinutes(time) {
  if (!time) return 0;

  const parts = time.split(':').map(Number);
  const [hours = 0, minutes = 0] = parts;

  return hours * 60 + minutes;
}

/**
 * Calculates remaining time for a project.
 *
 * @param {Array} tasks
 * @param {Date} now
 * @returns {number} Remaining time in minutes (can be negative).
 */
export function calculateProjectTimeLeft(tasks, now) {
  return tasks.reduce((total, task) => {
    if (isTaskCompleted(task.currentWorkingStatus)) {
      return total;
    }

    const allocated = timeStringToMinutes(task.totalTime);
    const used = timeStringToMinutes(task.timeUsed);

    let running = 0;

    if (isTaskInProgress(task.currentWorkingStatus) && task.lastPlay) {
      const lastPlay = new Date(task.lastPlay);
      if (!isNaN(lastPlay.getTime())) {
        running = Math.max(0, Math.floor((now.getTime() - lastPlay.getTime()) / 60000));
      }
    }

    return total + allocated - used - running;
  }, 0);
}

/**
 * Formats minutes into a human-readable time-left string.
 *
 * @param {number} minutes
 * @returns {string}
 */
export function formatTimeLeft(minutes) {
  const abs = Math.abs(minutes);
  const hrs = Math.floor(abs / 60);
  const mins = abs % 60;

  const formatted = `${hrs.toString().padStart(2, '0')}hrs : ${mins
    .toString()
    .padStart(2, '0')}mins`;

  return minutes >= 0 ? `${formatted} left` : `-${formatted} (Overtime)`;
}

/**
 * Returns true if task time is frozen and controls are disabled.
 *
 * @param {string} status
 * @returns {boolean}
 */
export function isTaskTerminal(status) {
  return (
    status === TASK_STATUS.SUBMITTED ||
    status === TASK_STATUS.COMPLETED ||
    status === TASK_STATUS.APPROVED
  );
}

/**
 * Returns true if task is currently running.
 *
 * @param {Object} task
 * @returns {boolean}
 */
export function isTaskRunning(task) {
  return task.currentWorkingStatus === TASK_STATUS.IN_PROGRESS && Boolean(task.lastPlay);
}

/**
 * Returns true if task can be submitted.
 */
export function canSubmitTask(task) {
  return isTaskRunning(task);
}

/**
 * Accumulates elapsed minutes since lastPlay into timeUsed.
 *
 * @param {string} timeUsed - HH:MM or HH:MM:SS
 * @param {string | null} lastPlay - ISO timestamp
 * @param {Date} now
 * @returns {string} Updated timeUsed (HH:MM)
 */
export function accumulateTimeUsed(timeUsed, lastPlay, now) {
  if (!lastPlay) return timeUsed ?? '00:00';

  const start = new Date(lastPlay);
  if (isNaN(start.getTime())) return timeUsed ?? '00:00';

  const elapsedMinutes = Math.floor((now.getTime() - start.getTime()) / 60000);
  if (elapsedMinutes <= 0) return timeUsed ?? '00:00';

  const [h = 0, m = 0] = (timeUsed ?? '00:00').split(':').map(Number);

  const total = h * 60 + m + elapsedMinutes;
  const hrs = Math.floor(total / 60);
  const mins = total % 60;

  return `${hrs.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;
}
